import type { BlogResult } from '@creanizer/types'
import { Calendar, User, Tag, MessageSquare, Heart, Share2 } from 'lucide-react'

interface BlogViewerProps {
  document: BlogResult
  imageAssets?: {
    avatars?: { [key: string]: string }
    headerImage?: string
  }
}

export function BlogViewer({ document, imageAssets }: BlogViewerProps) {
  // authorMappingからアバター画像を取得
  const getAuthorAvatar = (): string | undefined => {
    if (!imageAssets?.avatars || !document.author?.name) return undefined
    
    // documentのauthorMappingを参照
    const authorMapping = (document as any).authorMapping || {}
    const originalName = authorMapping[document.author.name]
    
    if (originalName && imageAssets.avatars[originalName]) {
      return imageAssets.avatars[originalName]
    }
    
    // フォールバック：直接マッチング
    return imageAssets.avatars[document.author.name]
  }
  
  // 日付フォーマット
  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return dateStr
    }
  }
  
  // Markdown風のテキストを簡易的にフォーマット
  const formatMarkdown = (text: string | undefined): React.ReactNode => {
    // テキストが存在しない場合は空の段落を返す
    if (!text) {
      return <p className="text-gray-600">コンテンツがありません</p>
    }
    
    // 段落で分割
    const paragraphs = text.split('\n\n')
    
    return paragraphs.map((paragraph, index) => {
      // 見出しのチェック
      if (paragraph.startsWith('# ')) {
        return <h3 key={index} className="text-2xl font-bold mt-6 mb-3">{paragraph.slice(2)}</h3>
      }
      if (paragraph.startsWith('## ')) {
        return <h4 key={index} className="text-xl font-bold mt-4 mb-2">{paragraph.slice(3)}</h4>
      }
      if (paragraph.startsWith('### ')) {
        return <h5 key={index} className="text-lg font-bold mt-3 mb-2">{paragraph.slice(4)}</h5>
      }
      
      // リスト
      if (paragraph.startsWith('- ') || paragraph.startsWith('* ')) {
        const items = paragraph.split('\n').map(item => item.slice(2))
        return (
          <ul key={index} className="list-disc list-inside space-y-1 my-4">
            {items.map((item, i) => (
              <li key={i} className="text-gray-700">{item}</li>
            ))}
          </ul>
        )
      }
      
      // 引用
      if (paragraph.startsWith('> ')) {
        return (
          <blockquote key={index} className="border-l-4 border-gray-300 pl-4 my-4 italic text-gray-600">
            {paragraph.slice(2)}
          </blockquote>
        )
      }
      
      // 通常の段落
      return (
        <p key={index} className="text-gray-700 leading-relaxed mb-4">
          {paragraph}
        </p>
      )
    })
  }
  
  return (
    <div className="max-w-4xl mx-auto">
      <article className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* ヘッダー画像 */}
        {imageAssets?.headerImage ? (
          <div className="h-64 relative">
            <img
              src={imageAssets.headerImage}
              alt={document.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
              <h1 className="text-4xl font-bold text-white text-center px-8 drop-shadow-lg">
                {document.title}
              </h1>
            </div>
          </div>
        ) : (
          <div className="h-64 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 relative">
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
              <h1 className="text-4xl font-bold text-white text-center px-8 drop-shadow-lg">
                {document.title}
              </h1>
            </div>
          </div>
        )}
        
        {/* メタ情報 */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {/* 著者情報 */}
              {document.author && (
                <div className="flex items-center gap-2">
                  {getAuthorAvatar() ? (
                    <img
                      src={getAuthorAvatar()!}
                      alt={document.author.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{document.author.name || '匿名'}</p>
                    {document.author.profile && (
                      <p className="text-xs text-gray-500">{document.author.profile}</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* 日付 */}
              <div className="flex items-center gap-1 text-gray-500">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">{formatDate(document.date)}</span>
              </div>
            </div>
            
            {/* カテゴリ・タグ */}
            <div className="flex items-center gap-2">
              {document.categories && document.categories.length > 0 && (
                <div className="flex gap-2">
                  {document.categories.map((category, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* タグ */}
          {document.tags && document.tags.length > 0 && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-gray-400" />
              {document.tags.map((tag, index) => (
                <span
                  key={index}
                  className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* 本文 */}
        <div className="p-8">
          {/* 要約 */}
          {document.summary && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border-l-4 border-gray-400">
              <p className="text-gray-700 italic">{document.summary}</p>
            </div>
          )}
          
          {/* 本文コンテンツ */}
          <div className="prose prose-lg max-w-none">
            {document.body ? formatMarkdown(document.body) : <p className="text-gray-500">記事の本文がありません</p>}
          </div>
        </div>
        
        {/* エンゲージメント */}
        <div className="px-8 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors">
                <Heart className="w-5 h-5" />
                <span className="text-sm">いいね</span>
              </button>
              
              <button className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
                <MessageSquare className="w-5 h-5" />
                <span className="text-sm">コメント</span>
              </button>
              
              <button className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors">
                <Share2 className="w-5 h-5" />
                <span className="text-sm">共有</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* コメントセクション */}
        {document.comments && document.comments.length > 0 && (
          <div className="p-6 border-t border-gray-200">
            <h3 className="font-bold text-gray-900 mb-4">
              コメント ({document.comments.length})
            </h3>
            
            <div className="space-y-4">
              {document.comments.map((comment, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{comment.author}</span>
                    <span className="text-sm text-gray-500">{comment.date}</span>
                  </div>
                  <p className="text-gray-700">{comment.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  )
}